import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import {
    TextField,
    Button,
    Typography,
    Box,
    Paper,
    IconButton,
    InputAdornment,
    LinearProgress
} from '@mui/material';

import { Visibility, VisibilityOff } from '@mui/icons-material';

// ----------------------
// Zod Schema Validation
// ----------------------
const RegistrationSchema = z.object({
    username: z.string().min(3, "Username must be at least 3 characters"),
    email: z.string().email("Invalid email format"),
    phone: z
        .string()
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number cannot exceed 15 digits")
        .regex(/^[0-9]+$/, "Phone number must contain only digits"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"]
});

// ----------------------
// Password Strength Function
// ----------------------
const getPasswordStrength = (password) => {
    let score = 0;

    if (!password) return { score: 0, label: "Weak", color: "#d32f2f" };

    if (password.length >= 6) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[\W_]/.test(password)) score++;

    let label = "Weak";
    let color = "#d32f2f"; // red

    if (score === 2) {
        label = "Fair";
        color = "#f57c00"; // orange
    } else if (score === 3) {
        label = "Good";
        color = "#fbc02d"; // yellow
    } else if (score >= 4) {
        label = "Strong";
        color = "#388e3c"; // green
    }

    return { score, label, color };
};

const AdminRegistration = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        label: "Weak",
        color: "#d32f2f"
    });

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: zodResolver(RegistrationSchema),
        mode: "onBlur"
    });

    // Watch password for strength updates
    const passwordValue = watch("password");
    React.useEffect(() => {
        setPasswordStrength(getPasswordStrength(passwordValue));
    }, [passwordValue]);

    const onSubmit = (data) => {
        // console.log("Form submitted:", data);
        // TODO: API integration
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                minHeight: '100vh',
                backgroundColor: '#f5f5f5',
                pt: 8
            }}
        >
            <Paper
                elevation={1}
                sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 450,
                    borderRadius: 2
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 3, color: '#0F1111' }}
                >
                    Admin Registration
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>

                    {/* Username */}
                    <TextField
                        label="Username"
                        fullWidth
                        margin="normal"
                        autoComplete="username"
                        {...register("username")}
                        error={!!errors.username}
                        helperText={errors.username?.message}
                    />

                    {/* Email */}
                    <TextField
                        label="Email"
                        type="email"
                        fullWidth
                        margin="normal"
                        autoComplete="email"
                        {...register("email")}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />

                    {/* Phone */}
                    <TextField
                        label="Phone Number"
                        fullWidth
                        margin="normal"
                        autoComplete="tel"
                        {...register("phone")}
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                    />

                    {/* Password + Strength Meter */}
                    <TextField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                        {...register("password")}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Strength meter bar */}
                    <Box mt={1} mb={2}>
                        <LinearProgress
                            variant="determinate"
                            value={(passwordStrength.score / 5) * 100}
                            sx={{
                                height: 8,
                                borderRadius: 1,
                                backgroundColor: "#e0e0e0",
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor: passwordStrength.color
                                }
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ color: passwordStrength.color, fontWeight: 600 }}
                        >
                            {passwordStrength.label}
                        </Typography>
                    </Box>

                    {/* Confirm Password */}
                    <TextField
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                        {...register("confirmPassword")}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Submit */}
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                            backgroundColor: '#ff9900',
                            color: '#fff',
                            padding: '6px 18px',
                            textTransform: 'none',
                            borderRadius: "20px",
                            fontWeight: 600,
                            mt: 2,
                            '&:hover': { backgroundColor: '#ff8800' }
                        }}
                    >
                        {isSubmitting ? "Submitting..." : "Register"}
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default AdminRegistration;
